<template>
  <div ref="mapContainer" class="mapbox-container"></div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiY2RnamRyIiwiYSI6ImNta2ZoeDV3dDAxamwzZnNnZ2o2ZTRmMWsifQ.vOeifDfBaLEOIRcQGSCTDg';
const DEFAULT_BAIDU_CENTER = [120.317229, 31.500645];

export default {
  name: 'MapboxMap',
  props: {
    companyList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      map: null,
      markers: [],
      popup: null,
      mapLoaded: false,
      resizeObserver: null,
    };
  },
  watch: {
    companyList() {
      this.renderMarkers();
    },
  },
  mounted() {
    this.initMap();
    this.observeContainerResize();
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.clearMarkers();
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    // 初始化地图；保留该方法供父页面在列表为空时重置地图。
    initMap() {
      if (this.map) {
        this.clearMarkers();
        this.map.resize();
        this.map.jumpTo({
          center: this.bd09ToWgs84(DEFAULT_BAIDU_CENTER),
          zoom: 12,
        });
        return;
      }

      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: this.bd09ToWgs84(DEFAULT_BAIDU_CENTER),
        zoom: 12,
      });
      this.map.on('load', () => {
        this.mapLoaded = true;
        this.map.resize();
        this.setMapLanguageToChinese();
        this.renderMarkers();
      });
    },

    // 首页会在 mounted 后重新计算内容区高度，地图画布需同步更新尺寸。
    observeContainerResize() {
      if (typeof ResizeObserver === 'undefined') return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.map) this.map.resize();
      });
      this.resizeObserver.observe(this.$refs.mapContainer);
    },

    // 将道路、区域和兴趣点等地名优先显示为简体中文。
    setMapLanguageToChinese() {
      const style = this.map && this.map.getStyle();
      const layers = (style && style.layers) || [];
      layers.forEach(layer => {
        const textField = layer.layout && layer.layout['text-field'];
        if (layer.type !== 'symbol' || !textField || JSON.stringify(textField).indexOf('name') === -1) return;

        this.map.setLayoutProperty(layer.id, 'text-field', [
          'coalesce',
          ['get', 'name_zh-Hans'],
          ['get', 'name_zh'],
          ['get', 'name'],
        ]);
      });
    },

    // 点击右侧企业列表时定位到地图标记并打开信息窗口。
    clickCompanyShowInfo(info) {
      if (!this.map || !this.mapLoaded) return;
      const coordinates = this.getCompanyCoordinates(info);
      if (!coordinates) return;

      this.map.easeTo({
        center: coordinates,
        zoom: 15,
        offset: [-200, 0],
        duration: 600,
      });
      this.openPopup(info, coordinates);
    },

    renderMarkers() {
      if (!this.map || !this.mapLoaded) return;

      this.clearMarkers();
      const companies = this.companyList
        .map(company => ({
          company,
          coordinates: this.getCompanyCoordinates(company),
        }))
        .filter(item => item.coordinates);

      companies.forEach(item => {
        const markerElement = this.createMarkerElement(item.company.status);
        markerElement.addEventListener('click', event => {
          // 防止同一次点击继续冒泡到地图，触发 Popup 的 closeOnClick 立即关闭。
          event.stopPropagation();
          this.openPopup(item.company, item.coordinates);
        });

        const marker = new mapboxgl.Marker({
          element: markerElement,
          anchor: 'bottom',
        })
          .setLngLat(item.coordinates)
          .addTo(this.map);

        this.markers.push(marker);
      });

      if (companies.length) {
        this.fitAllMarkers(companies.map(item => item.coordinates));
      }
    },

    // 自动缩放至所有企业图标可见，并为右侧企业列表预留显示空间。
    fitAllMarkers(coordinatesList) {
      if (!coordinatesList.length) return;
      if (coordinatesList.length === 1) {
        this.map.easeTo({
          center: coordinatesList[0],
          zoom: 13,
          offset: [-200, 0],
          duration: 0,
        });
        return;
      }

      const bounds = coordinatesList.reduce(
        (mapBounds, coordinates) => mapBounds.extend(coordinates),
        new mapboxgl.LngLatBounds(coordinatesList[0], coordinatesList[0]),
      );
      this.map.fitBounds(bounds, {
        padding: {
          top: 50,
          bottom: 50,
          left: 50,
          right: 460,
        },
        maxZoom: 15,
        duration: 0,
      });
    },

    createMarkerElement(status) {
      let icon = require('@/assets/qiye1.png');
      if (status == 0) {
        icon = require('@/assets/qiye2.png');
      } else if (status == 1) {
        icon = require('@/assets/qiye3.png');
      }

      const element = document.createElement('button');
      element.type = 'button';
      element.className = 'mapbox-company-marker';
      element.setAttribute('aria-label', '查看企业信息');

      // 动画放在内部元素上，避免覆盖 Mapbox 写在 Marker 外层的定位 transform。
      const iconElement = document.createElement('span');
      iconElement.className = 'mapbox-company-marker-icon';
      if (status == 1) iconElement.classList.add('mapbox-company-marker-warning');
      iconElement.style.backgroundImage = `url(${icon})`;
      element.appendChild(iconElement);
      return element;
    },

    openPopup(company, coordinates) {
      if (this.popup) this.popup.remove();
      this.popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: '380px',
        // 图标以底部为坐标锚点：弹窗在上方时避开图标，在下方时无需额外留白。
        offset: {
          top: [0, 0],
          'top-left': [0, 0],
          'top-right': [0, 0],
          bottom: [0, -35],
          'bottom-left': [0, -35],
          'bottom-right': [0, -35],
          left: [18, -18],
          right: [-18, -18],
        },
        className: 'company-info-popup',
      })
        .setLngLat(coordinates)
        .setDOMContent(this.createPopupContent(company))
        .addTo(this.map);
    },

    createPopupContent(company) {
      const wrapper = document.createElement('div');
      wrapper.className = 'mapbox-company-popup';

      const title = document.createElement('div');
      title.className = 'mapbox-company-popup-title';
      title.textContent = company.company_name || '-';
      wrapper.appendChild(title);

      const table = document.createElement('table');
      table.className = 'mapbox-company-popup-table';
      const tbody = document.createElement('tbody');
      const fields = [
        ['行业', company.industry],
        ['联系人', company.legalPerson],
        ['电话', company.phone],
        ['地址', company.address],
        ['经营范围', company.information],
      ];
      fields.forEach(field => {
        const row = document.createElement('tr');
        row.className = 'mapbox-company-popup-row';
        const label = document.createElement('td');
        label.className = 'mapbox-company-popup-label';
        label.textContent = `${field[0]}：`;
        const value = document.createElement('td');
        value.className = 'mapbox-company-popup-value';
        value.textContent = field[1] || '-';
        row.appendChild(label);
        row.appendChild(value);
        tbody.appendChild(row);
      });
      table.appendChild(tbody);
      wrapper.appendChild(table);
      return wrapper;
    },

    getCompanyCoordinates(company) {
      if (!company) return null;
      const location = company.wz || company.point || {};
      const source = location.lng !== undefined && location.lat !== undefined ? [location.lng, location.lat] : [];
      const lng = Number(source[0]);
      const lat = Number(source[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      return this.bd09ToWgs84([lng, lat]);
    },

    clearMarkers() {
      this.markers.forEach(marker => marker.remove());
      this.markers = [];
      if (this.popup) {
        this.popup.remove();
        this.popup = null;
      }
    },

    // 后端保存的是百度地图 BD-09 坐标，Mapbox 使用 WGS-84，需要先转换。
    bd09ToWgs84(coordinates) {
      const gcj02 = this.bd09ToGcj02(coordinates);
      return this.gcj02ToWgs84(gcj02);
    },

    bd09ToGcj02(coordinates) {
      const x = Number(coordinates[0]) - 0.0065;
      const y = Number(coordinates[1]) - 0.006;
      const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * Math.PI);
      const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * Math.PI);
      return [z * Math.cos(theta), z * Math.sin(theta)];
    },

    gcj02ToWgs84(coordinates) {
      const lng = Number(coordinates[0]);
      const lat = Number(coordinates[1]);
      if (this.outOfChina(lng, lat)) return [lng, lat];
      const delta = this.coordinateDelta(lng, lat);
      return [lng * 2 - delta[0], lat * 2 - delta[1]];
    },

    coordinateDelta(lng, lat) {
      const a = 6378245.0;
      const ee = 0.006693421622965943;
      let dLat = this.transformLat(lng - 105.0, lat - 35.0);
      let dLng = this.transformLng(lng - 105.0, lat - 35.0);
      const radLat = (lat / 180.0) * Math.PI;
      let magic = Math.sin(radLat);
      magic = 1 - ee * magic * magic;
      const sqrtMagic = Math.sqrt(magic);
      dLat = (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * Math.PI);
      dLng = (dLng * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * Math.PI);
      return [lng + dLng, lat + dLat];
    },

    transformLat(x, y) {
      let result = -100 + 2 * x + 3 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
      result += ((20 * Math.sin(6 * x * Math.PI) + 20 * Math.sin(2 * x * Math.PI)) * 2) / 3;
      result += ((20 * Math.sin(y * Math.PI) + 40 * Math.sin((y / 3) * Math.PI)) * 2) / 3;
      result += ((160 * Math.sin((y / 12) * Math.PI) + 320 * Math.sin((y * Math.PI) / 30)) * 2) / 3;
      return result;
    },

    transformLng(x, y) {
      let result = 300 + x + 2 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
      result += ((20 * Math.sin(6 * x * Math.PI) + 20 * Math.sin(2 * x * Math.PI)) * 2) / 3;
      result += ((20 * Math.sin(x * Math.PI) + 40 * Math.sin((x / 3) * Math.PI)) * 2) / 3;
      result += ((150 * Math.sin((x / 12) * Math.PI) + 300 * Math.sin((x / 30) * Math.PI)) * 2) / 3;
      return result;
    },

    outOfChina(lng, lat) {
      return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
    },
  },
};
</script>

<style>
.mapbox-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: 'Microsoft YaHei', sans-serif;
}

.mapbox-company-marker {
  width: 35px;
  height: 35px;
  padding: 0;
  border: 0;
  overflow: visible;
  background: transparent;
  cursor: pointer;
}

.mapbox-company-marker-icon {
  display: block;
  width: 35px;
  height: 35px;
  background: transparent center/contain no-repeat;
  pointer-events: none;
}

.mapbox-company-marker-warning {
  animation: mapbox-marker-warning-bounce 0.55s infinite alternate ease-in-out;
}

.mapbox-company-popup {
  width: 360px;
  color: #333;
  font: 14px/20px Arial, 'Microsoft YaHei', sans-serif;
}

.mapbox-company-popup-title {
  padding: 10px 34px 0 10px;
  color: #0064ff;
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;
}

.mapbox-company-popup-table {
  width: 100%;
  padding: 5px 5px 10px 10px;
  border-spacing: 0;
  table-layout: fixed;
}

.mapbox-company-popup-row {
  vertical-align: top;
}

.mapbox-company-popup-label {
  width: 70px;
  padding: 0;
  line-height: 20px;
  vertical-align: top;
  white-space: nowrap;
}

.mapbox-company-popup-value {
  padding: 0;
  line-height: 20px;
  vertical-align: top;
  word-break: break-word;
}

.company-info-popup.mapboxgl-popup {
  z-index: 5;
}

.company-info-popup .mapboxgl-popup-content {
  width: 360px;
  padding: 0;
  overflow: hidden;
  border: 1px solid #ccc;
  border-radius: 2px;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.35);
}

.company-info-popup .mapboxgl-popup-close-button {
  top: 5px;
  right: 5px;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  box-shadow: none;
  color: #666;
  font-size: 22px;
  line-height: 24px;
}

.company-info-popup .mapboxgl-popup-close-button:focus,
.company-info-popup .mapboxgl-popup-close-button:active {
  border: 0;
  outline: none;
  box-shadow: none;
}

.company-info-popup .mapboxgl-popup-close-button:hover {
  color: #333;
  background: #f2f2f2;
}

.company-info-popup.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  border-top-color: #ccc;
}

@keyframes mapbox-marker-warning-bounce {
  from {
    transform: translateY(0);
    filter: drop-shadow(0 1px 1px rgba(231, 45, 20, 0.25));
  }
  to {
    transform: translateY(-9px);
    filter: drop-shadow(0 8px 5px rgba(231, 45, 20, 0.45));
  }
}
</style>
